import { useMutation } from '@apollo/client';
import React, { FC, useCallback, useState } from 'react';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Form, { FormFooter, FormHeader } from '@atlaskit/form';
import { Task, UpdateStatus } from '@css-xp/apd-common';
import { ErrorComponents, Icon } from '@css-xp/base';
import { useApplicationContext } from '@css-xp/base/common-context';
import { SlackChannelLink } from '../common';
import { ModalTitle } from '../constants/modal-titles';
import { changeAssignee as CHANGE_ASSIGNEE } from '../queries/tasks';
import ErrorNotificationMessage from './error-notification-message';
import './modal.scss';
import { useModalNotification } from './use-modal-notification';

interface ChangeAssigneeModalProps {
  taskList: Task[];
  assignee: string;
  onComplete?: () => void;
  closeModal: () => void;
  assigneeName: string;
}

const ChangeAssigneeModal: FC<ChangeAssigneeModalProps> = ({
  taskList,
  assignee,
  onComplete,
  closeModal,
  assigneeName,
}) => {
  const [loading, setLoading] = useState<boolean>(false);

  const [changeAssignee] = useMutation<{ changeAssignee: { status: UpdateStatus } }>(CHANGE_ASSIGNEE);

  const userProfile = useApplicationContext();
  const taskIds = taskList.map((item) => item?.taskId);

  const [renderSuccessNotification, renderErrorNotification] = useModalNotification(
    closeModal,
    'change-assignee-modal-notification'
  );

  const handleFormSubmit = useCallback(async () => {
    setLoading(true);
    try {
      const userID = userProfile?.userId?.split('@')?.[0];
      const result = await changeAssignee({
        variables: {
          assignee: assignee,
          id: userID,
          taskIdList: taskIds,
          groupName: taskList?.[0].groupId,
          requestType: taskList?.[0].requestType,
        },
      });
      const errorMsg = result?.errors?.[0]?.extensions?.message;
      if (errorMsg) {
        throw new Error(errorMsg);
      }
      const status = result?.data?.changeAssignee?.status ?? '';
      if (status === 'OK') {
        onComplete?.();
        renderSuccessNotification(
          'Action complete',
          <p>You've changed the assignee of the selected {taskIds.length > 1 ? 'requests' : 'request'}.</p>
        );
      }
    } catch (e: any) {
      renderErrorNotification(
        `We’re unable to reassign the ${taskIds.length > 1 ? 'requests' : 'request'}`,
        <>
          <ErrorNotificationMessage errorMessage={e.message} /> Having trouble? Contact the{' '}
          <ErrorComponents.ChatRoomLink /> channel.
        </>
      );
    } finally {
      setLoading(false);
    }
  }, [taskList, assignee, onComplete, closeModal]);

  return (
    <Form onSubmit={handleFormSubmit}>
      {({ formProps }) => (
        <div className="modal-form-wrapper" data-testid="reassign-modal">
          <form {...formProps}>
            <FormHeader
              title={
                <div className="modal-title-wrapper">
                  <span className="icon">
                    <Icon type="warning" size="medium" />
                  </span>
                  <span className="modal-title">{ModalTitle.CHANGE_ASSIGNEE + ' ' + assigneeName + '?'}</span>
                </div>
              }
            />
            <p>
              The selected requests will be reassigned, and this action will be added to the requests in the Slack
              channel{' '}
              <SlackChannelLink url={taskList?.[0]?.slackMessageUrl}>{taskList?.[0]?.slackChannel}</SlackChannelLink>{' '}
              and Approval Desk.
            </p>
            <FormFooter align="end">
              <ButtonGroup>
                <Button appearance="subtle" onClick={closeModal} testId="reassign-cancel-btn">
                  Cancel
                </Button>
                <LoadingButton
                  appearance="primary"
                  autoFocus
                  type="submit"
                  isLoading={loading}
                  testId="reassign-submit-btn"
                >
                  Reassign
                </LoadingButton>
              </ButtonGroup>
            </FormFooter>
          </form>
        </div>
      )}
    </Form>
  );
};

export default ChangeAssigneeModal;
